import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/Chrome";
import { api } from "@/lib/api";
import { PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Conversation() {
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();
  const { data: history = [] } = useQuery({ queryKey: ["history"], queryFn: api.agent.history, refetchInterval: 4000 });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history.length]);

  const chat = useMutation({
    mutationFn: (text) => api.agent.chat(text),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["activity"] });
      qc.invalidateQueries({ queryKey: ["approvals", "pending"] });
      qc.invalidateQueries({ queryKey: ["memory"] });
    },
    onError: (e) => toast.error(`Agent error: ${e.message}`),
  });

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    setBusy(true);
    await chat.mutateAsync(text);
    setBusy(false);
  };

  return (
    <div data-testid="conversation-page" className="flex flex-col h-screen">
      <PageHeader
        eyebrow="Live channel"
        title="Talk to Copilot"
        subtitle="This mirror is exactly what your Telegram thread looks like. Try natural language — 'summarize my inbox', 'move my 3pm', or 'remember: always CC my co-founder on investor emails'."
      />
      <div className="flex-1 overflow-y-auto px-10 py-6">
        {history.length === 0 && (
          <div className="max-w-xl">
            <div className="text-sm text-stone-400">Copilot is ready. Say hi or ask it about your calendar or inbox.</div>
            <div className="mt-4 grid gap-2">
              {["Summarize my priority email", "What's on my calendar today?", "Send an email to sarah@sequoia.com saying we can meet Thursday", "remember: always CC my co-founder on investor emails"].map(s => (
                <button key={s} data-testid={`suggestion-${s.slice(0,10)}`}
                  onClick={() => setInput(s)}
                  className="text-left text-xs font-mono px-3 py-2 border border-stone-800 hover:border-orange-700 hover:bg-stone-900/60 text-stone-400 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <ul className="space-y-4 max-w-3xl" data-testid="chat-history">
          {history.map((m) => (
            <li key={`${m.ts}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-3 text-sm leading-relaxed
                ${m.role === "user" ? "bg-orange-600 text-white" : "bg-stone-900 border border-stone-800 text-stone-100"}`}>
                <div className="whitespace-pre-wrap">{m.text}</div>
                <div className={`mt-1 text-[10px] font-mono uppercase tracking-widest ${m.role === "user" ? "text-orange-100/70" : "text-stone-500"}`}>
                  {m.role === "user" ? "you" : "copilot"} · {new Date(m.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </li>
          ))}
          <div ref={endRef} />
        </ul>
      </div>

      <div className="border-t border-stone-800 px-10 py-5 bg-[#0c0a09]">
        <div className="flex gap-2 max-w-3xl">
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Message Copilot…"
            className="flex-1 bg-stone-900 border border-stone-800 focus:border-orange-600 outline-none px-4 py-2.5 text-sm text-stone-100 font-sans transition-colors"
            disabled={busy}
          />
          <button
            data-testid="chat-send"
            onClick={send}
            disabled={busy || !input.trim()}
            className="px-4 py-2.5 bg-orange-600 hover:bg-orange-500 disabled:bg-stone-800 disabled:text-stone-600 text-white text-xs uppercase tracking-widest font-mono flex items-center gap-2 transition-colors"
          >
            {busy ? "…" : <><PaperPlaneRight size={14} weight="bold" /> Send</>}
          </button>
        </div>
      </div>
    </div>
  );
}
